import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { serializeParams } from '../utils/params';

/**
 * 處理「先拿 count → 修正頁碼 → 再拿分頁資料」的共用流程。
 * 自帶 AbortController，component unmount / 重新 fetch 時會取消前一個請求。
 *
 * @param {Object} opts
 *   - dataUrl, countUrl: API endpoint
 *   - token: Bearer token
 *   - enabled: 是否啟用（false 時不會 fetch）
 *   - params: 共同 query params（包含 start_date/end_date 等；array 會用 repeat 格式）
 *   - page, pageSize
 *   - setTotalCount: 從 pagination hook 來
 *   - setError: 外部 error state
 */
export function useFetchPaginatedData({
  dataUrl,
  countUrl,
  token,
  enabled,
  params,
  page,
  pageSize,
  setTotalCount,
  setError,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const controllerRef = useRef(null);

  const paramsKey = JSON.stringify(params || {});

  const runFetch = useCallback(async () => {
    if (!enabled || !token || !dataUrl || !countUrl) return;

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;

    setLoading(true);
    try {
      const skip = (page - 1) * pageSize;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
        paramsSerializer: serializeParams,
        signal: controller.signal,
      };

      const countRes = await axios.get(countUrl, { ...config, params });
      if (controller.signal.aborted) return;
      const newTotalCount = countRes.data.count || 0;
      setTotalCount(newTotalCount);

      const newTotalPages = Math.ceil(newTotalCount / pageSize);
      if (page > newTotalPages && newTotalPages > 0) {
        setData([]);
        setLoading(false);
        return;
      }

      const dataRes = await axios.get(dataUrl, {
        ...config,
        params: { ...params, skip, limit: pageSize },
      });
      if (controller.signal.aborted) return;

      if (dataRes.data.error) {
        setError(dataRes.data.error);
        setData([]);
      } else {
        setData(Array.isArray(dataRes.data.data) ? dataRes.data.data : []);
      }
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError' || err.name === 'AbortError') return;
      console.error('Fetch data error:', err.response?.data, err.message);
      setError(err.response?.data?.detail || err.message);
      setData([]);
      setTotalCount(0);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, token, dataUrl, countUrl, page, pageSize, paramsKey, setTotalCount, setError]);

  useEffect(() => {
    runFetch();
    return () => {
      controllerRef.current?.abort();
    };
  }, [runFetch]);

  return { data, loading, refetch: runFetch };
}
